import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import { useQueryClient } from '@tanstack/react-query';
import { useApplicationQuery, useClientQuery } from 'hooks/webapi';
import { PENDING } from 'Router/routes';
import LoadingAnimation from 'assets/lottie/loading-vertical-lines.json';
import { useClientApplicationCheckStatusQuery } from 'hooks/webapi/client/useClientApplicationCheckStatusQuery';
import useNavigateByApplication from 'pages/Waiting/useNavigateByApplication';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import IdentifyEvent from 'hooks/context/EventCollector/events/IdentifyEvent';

const LottieOptions = {
  loop: true,
  autoplay: true,
  animationData: LoadingAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

export default function Waiting() {
  const { t } = useTranslation('waiting');
  const { navigateNext } = useNavigateByApplication();
  const { identify } = useEventCollector();

  const queryClient = useQueryClient();
  const { refetch: refetchClient } = useClientQuery();

  const { data: checkStatusData } = useClientApplicationCheckStatusQuery(
    true,
    5000,
  );
  const navigate = useNavigate();

  const { refetch } = useApplicationQuery(checkStatusData?.completed);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: ['client', 'application', 'check-status'],
      });
      queryClient.removeQueries({ queryKey: ['client', 'application'] });
    };
  }, [queryClient]);

  useEffect(() => {
    async function getApplicationAndNavigate() {
      const { data: application } = await refetch();
      const { data: client } = await refetchClient();

      if (application && client) {
        const navigated = navigateNext(application, client);
        identify(new IdentifyEvent({ application }));

        if (navigated) {
          return;
        }

        // there isn't any navigation by application because user didn't fill the bank information (Manual flow)

        navigate(PENDING);
      }
    }

    if (!checkStatusData?.completed) {
      return;
    }

    getApplicationAndNavigate();
  }, [checkStatusData?.completed, identify, navigateNext, refetch]);

  return (
    <>
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      <div className="pointer-events-none" data-test="waiting-lottie-wrapper">
        <Lottie width={350} options={LottieOptions} isClickToPauseDisabled />
      </div>

      <h1
        className="text-center text-4xl font-semibold mb-6"
        data-test="waiting-title"
      >
        {t('title')}
      </h1>
      <p className="text-center max-w-[420px] mx-auto" data-test="waiting-info">
        {t('info')}
      </p>
    </>
  );
}
